import { useEffect, useRef, useState, useCallback } from 'react';
import { API_BASE_URL } from '@/lib/config';

export interface TaskProgress {
  task_id: string;
  status: string;
  step: string;
  progress: number;
  message?: string;
  attempt?: number;
  strategy?: string;
  strategyLabel?: string;
  phase?: number;
  phaseLabel?: string;
  totalPhases?: number;
  error?: string;
  /** iframe pipeline progress event type */
  iframe_step?: string;
  /** task priority — included when urgent (from priority-crawl) */
  priority?: string;
}

/**
 * SSE hook to receive real-time task progress from /api/serp/sse
 * Returns a Map of task_id -> latest TaskProgress
 */
export function useTaskSSE() {
  const [progressMap, setProgressMap] = useState<Map<string, TaskProgress>>(new Map());
  const esRef = useRef<EventSource | null>(null);

  const connect = useCallback(() => {
    if (esRef.current) return;

    const url = `${API_BASE_URL}/serp/sse`;
    const es = new EventSource(url);
    esRef.current = es;

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as Record<string, unknown>;
        if ((data.type === 'task_progress' || data.type === 'iframe_progress') && data.task_id) {
          const tp: TaskProgress = {
            task_id: data.task_id as string,
            status: (data.status as string) || 'processing',
            step: (data.step as string) || '',
            progress: (data.progress as number) || 0,
            message: data.message as string | undefined,
            attempt: data.attempt as number | undefined,
            strategy: data.strategy as string | undefined,
            strategyLabel: data.strategyLabel as string | undefined,
            phase: data.phase as number | undefined,
            phaseLabel: data.phaseLabel as string | undefined,
            totalPhases: data.totalPhases as number | undefined,
            error: data.error as string | undefined,
            iframe_step: data.iframe_step as string | undefined,
            priority: data.priority as string | undefined,
          };
          setProgressMap(prev => {
            const next = new Map(prev);
            // Remove completed/failed tasks after 5s
            if (tp.status === 'completed' || tp.status === 'failed') {
              setTimeout(() => {
                setProgressMap(p => { const n = new Map(p); n.delete(tp.task_id); return n; });
              }, 5000);
            }
            next.set(tp.task_id, tp);
            return next;
          });
        }
      } catch {
        // Ignore parse errors
      }
    };

    es.onerror = () => {
      es.close();
      esRef.current = null;
      // Reconnect after 5s
      setTimeout(connect, 5000);
    };
  }, []);

  useEffect(() => {
    connect();
    return () => {
      esRef.current?.close();
      esRef.current = null;
    };
  }, [connect]);

  return progressMap;
}
